/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { forwardRef, Fragment, type JSX } from "react";

import { spacing } from "../../../../../global/emotion";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import type { CSSColorStrict } from "../../../../utils/colors/types/CSSColorStrict.types";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import { PlaceholderIcon } from "../../icons/Icon/GeneralIcons";
import type { IconElement } from "../../icons/Icon/Icon";
import { ChevronRightIcon } from "../../icons/Icon/NavigationIcons";
import { AvatarCommunity } from "../AvatarCommunity/AvatarCommunity.emotion";
import {
  baseStyle,
  contentStyle,
  disabledStyle,
  imageStyle,
  metaDataStyle,
} from "../listStyles.emotion";
import { Text } from "../Text/Text.emotion";

const hallowElement: HallowElement = "div";

const contentInnerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.t,
};

const contentOuterStyle = [contentStyle, { gap: spacing.xxs }];

const metaDataItemStyle: Interpolation = {
  width: "fit-content",
};

export type ListContentProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  color?: CSSColorStrict;
  description?: string;
  disabled?: boolean;
  icon?: IconElement;
  imageSrc?: string;
  isCommunity?: boolean;
  metaData?: string[];
  metaDataIcon?: IconElement;
  title: string;
};

export const ListContent = forwardRef<any, ListContentProps>(
  (
    {
      asChild = false,
      children,
      color,
      description,
      disabled,
      icon = <ChevronRightIcon />,
      imageSrc,
      isCommunity = false,
      metaData,
      metaDataIcon = <PlaceholderIcon />,
      title,
      ...props
    },
    ref,
  ) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const DeterminedChildren = determineChildrenInject({
      afterChildrenInject: [
        isCommunity ? (
          <AvatarCommunity
            color={color}
            imgSrc={imageSrc}
            key="image"
            size="m"
          />
        ) : (
          <img alt={description} key="image" src={imageSrc} css={imageStyle} />
        ),
        <span key="content" css={contentOuterStyle}>
          <span css={contentInnerStyle}>
            <Text overflow="ellipsis" size="l" type="title">
              {title}
            </Text>
            {description && (
              <Text overflow="ellipsis" size="s" type="body">
                {description}
              </Text>
            )}
          </span>
          {metaData?.length > 0 && (
            <span css={metaDataStyle}>
              {metaData.map((metaDataItem, index) => (
                <Fragment key={`${metaDataItem}${index}`}>
                  <Text
                    overflow="ellipsis"
                    size="l"
                    type="detail"
                    css={metaDataItemStyle}
                  >
                    {metaDataItem}
                  </Text>
                  {index < metaData.length - 1 && metaDataIcon}
                </Fragment>
              ))}
            </span>
          )}
        </span>,
        <Fragment key="icon">{icon}</Fragment>,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement
        ref={ref}
        css={[baseStyle, disabled && disabledStyle]}
        {...{ disabled, ...props }}
      >
        {DeterminedChildren}
      </DeterminedElement>
    );
  },
);

ListContent.displayName = "ListContent";
